import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, List, ListItem, ListItemButton, ListItemIcon } from '@mui/material';
import { Button, Flex, Loading, SubPage, Typo } from '../../../ui';
import { useSettings } from '../hooks/useSettingsState';
import { useSettingsActions } from '../hooks/useSettingsActions';
import { useIsAdmin, useIsIncarnated } from '../../auth/hooks';

// Permanent features - stable features available to clients
const PERMANENT_FEATURES = [
  { key: 'diplomas', adminOnly: false }, // sequence of courses from package
  { key: 'dripContent', adminOnly: false }, // sequence of courses from package
  { key: 'quiz', adminOnly: false }, // sequence of courses from package
  { key: 'showTaskResultOnAnswer', adminOnly: false }, // prevent showing previous responses
  { key: 'packageSequence', adminOnly: true }, // sequence of courses from package
  { key: 'courseCategories', adminOnly: true }, // categories for courses
  { key: 'giftCodeExpiration', adminOnly: true }, // gift code expiration visibility
  { key: 'klarnaCheckout', adminOnly: true },
  { key: 'enterpriseStore', adminOnly: true },
];

// Deprecated features - will be removed from BE if present
const DEPRECATED_FEATURES = ['newFixedMenu', 'deleteMediaFolders', 'diplomaDownload', 'newCourseView', 'newTaskView'];

const PERMANENT_FEATURE_KEYS = new Set(PERMANENT_FEATURES.map((f) => f.key));

// Filter features list based on admin/incarnated status
const filterFeaturesByVisibility = (featureList, canSeeAdminOnly) => {
  return featureList.filter((feature) => !feature.adminOnly || canSeeAdminOnly);
};

// Reusable feature list component
const FeatureList = ({ features, localFeatures, onToggle, t }) => (
  <List sx={{ width: '100%', maxWidth: 360 }}>
    {features.map(({ key }) => {
      const labelId = `checkbox-list-label-${key}`;
      return (
        <ListItem key={key} disablePadding>
          <ListItemButton role={undefined} onClick={() => onToggle(key)} dense>
            <ListItemIcon>
              <Checkbox edge='start' checked={!!localFeatures[key]} tabIndex={-1} disableRipple inputProps={{ 'aria-labelledby': labelId }} />
            </ListItemIcon>
            <Typo.Text>{t(`settings.${key}`)}</Typo.Text>
          </ListItemButton>
        </ListItem>
      );
    })}
  </List>
);

const Features = () => {
  const { t } = useTranslation();
  const { isLoading, settings } = useSettings();
  const features = settings?.features;
  const { updateSettings } = useSettingsActions();

  const isIncarnated = useIsIncarnated();
  const isAdmin = useIsAdmin();
  const canSeeAdminOnly = isIncarnated || isAdmin;

  const [localFeatures, setLocalFeatures] = useState({});

  // Initialize local features from BE data
  useEffect(() => {
    if (!features) return;
    const initializedFeatures = { ...features };

    // Initialize all known permanent features with false if not present
    PERMANENT_FEATURES.forEach(({ key }) => {
      if (!(key in initializedFeatures)) {
        initializedFeatures[key] = false;
      }
    });

    // Remove deprecated features from local state
    DEPRECATED_FEATURES.forEach((key) => {
      delete initializedFeatures[key];
    });

    setLocalFeatures(initializedFeatures);
  }, [features]);

  // Filter features based on visibility
  const visiblePermanentFeatures = useMemo(() => filterFeaturesByVisibility(PERMANENT_FEATURES, canSeeAdminOnly), [canSeeAdminOnly]);

  const handleToggle = (key) => {
    setLocalFeatures((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    const featuresToSave = {};

    // Preserve non-permanent features (beta) that are currently enabled on the server
    if (features) {
      Object.entries(features).forEach(([key, value]) => {
        if (!PERMANENT_FEATURE_KEYS.has(key) && !DEPRECATED_FEATURES.includes(key) && value === true) {
          featuresToSave[key] = true;
        }
      });
    }

    // Add permanent features from local state
    Object.entries(localFeatures).forEach(([key, value]) => {
      if (DEPRECATED_FEATURES.includes(key)) return;
      if (PERMANENT_FEATURE_KEYS.has(key) && value === true) {
        featuresToSave[key] = true;
      }
    });

    await updateSettings({ features: featuresToSave });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <SubPage gap='20' align='flex-start'>
      {visiblePermanentFeatures.length > 0 && (
        <Flex gap='10' direction='column' align='flex-start'>
          <Typo.TextTitle>{t('settings.feature_title')}</Typo.TextTitle>
          <Typo.MediumText>{t('settings.feature_description')}</Typo.MediumText>
          <FeatureList features={visiblePermanentFeatures} localFeatures={localFeatures} onToggle={handleToggle} t={t} />
        </Flex>
      )}
      <Flex direction='row' gap='10' width='100%' justify='flex-end'>
        <Button onClick={handleSave}>{t('globals.save')}</Button>
      </Flex>
    </SubPage>
  );
};

export default Features;
